import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { expenseUserStatusLabel, ticketAmountLine } from "./expenseUserI18n";
import {
  getExpenseUserNote,
  openExpenseUserNotePdf,
  deleteExpenseUserNote,
  createExpenseUserAdvance,
  deleteExpenseUserAdvance,
  patchExpenseUserAdvance,
  createExpenseUserPayment,
  deleteExpenseUserPayment,
} from "../../api/ExpensesUser/expenseUserNotesApi";
import {
  createExpenseUserDocument,
  patchExpenseUserDocument,
} from "../../api/ExpensesUser/expenseUserDocumentsApi";
import {
  getExpenseUserStatusChipProps,
  KIND_STAFF,
  KIND_SUPPLIER,
} from "./expenseUserStatusChips";
import {
  hasPermisoVerTodoGastosToken,
  isCompanyCardTicket,
  ticketPaymentChipProps,
  ticketPaymentTypeChipProps,
} from "./expenseUserPaymentChips";
import { expenseUserPageShellSx } from "./expenseUserLayout";
import ExpenseUserWebcamDialog from "./ExpenseUserWebcamDialog";
import {
  pageFieldSx,
  pageHeroShellSx,
  pageContentCardSx,
  pageMobileCardSx,
} from "../../styles/pageModernStyles";

/** En escritorio, `capture` hace que el navegador espere cámara; en móvil abre cámara directa. */
const shouldUseCameraCapture = () =>
  typeof navigator !== "undefined" &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent || "",
  );

const ExpenseUserNoteDetail = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { noteId } = useParams();
  const navigate = useNavigate();
  const { contentMargin, isMobile } = useContext(SidebarContext);

  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState(KIND_STAFF);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [payBusyId, setPayBusyId] = useState(null);
  const [removeDialogDoc, setRemoveDialogDoc] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [noteTab, setNoteTab] = useState(0);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const isAdminGastos = hasPermisoVerTodoGastosToken();

  const idNum = noteId && Number.parseInt(String(noteId), 10);
  const valid = Number.isInteger(idNum) && idNum > 0;

  const load = useCallback(async () => {
    if (!valid) {
      setError(t("ExpenseUser.note.errInvalid"));
      setNote(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await getExpenseUserNote(idNum);
      setNote(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        setError(t("ExpenseUser.note.errNotFound"));
      } else if (e?.response?.status === 403) {
        setError(t("ExpenseUser.note.err403"));
      } else {
        setError(
          e?.response?.data?.error ||
            e.message ||
            t("ExpenseUser.note.errLoad"),
        );
      }
      setNote(null);
    } finally {
      setLoading(false);
    }
  }, [idNum, valid, t]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const onPdf = async () => {
    if (!valid) return;
    try {
      setPdfBusy(true);
      setError(null);
      await openExpenseUserNotePdf(idNum);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errOpenPdf"),
      );
    } finally {
      setPdfBusy(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target?.files?.[0];
    if (e.target) e.target.value = "";
    await uploadTicketFile(file);
  };

  const setTicketPayment = async (docId, paymentStatus) => {
    try {
      setPayBusyId(docId);
      setError(null);
      await patchExpenseUserDocument(docId, { paymentStatus });
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.error || e.message || t("ExpenseUser.note.errPay"),
      );
    } finally {
      setPayBusyId(null);
    }
  };

  const formatEuro = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "EUR",
      }).format(v);
    } catch {
      return `${v.toFixed(2)} €`;
    }
  };

  const todayIso = () => new Date().toISOString().slice(0, 10);

  const uploadTicketFile = async (file) => {
    if (!file || !valid) return;
    try {
      setUploading(true);
      setError(null);
      await createExpenseUserDocument(kind, file, {
        expenseUserNoteId: idNum,
      });
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errUpload"),
      );
    } finally {
      setUploading(false);
    }
  };

  const addAdvanceRow = async () => {
    if (!valid) return;
    try {
      setLedgerBusy(true);
      setError(null);
      await createExpenseUserAdvance(idNum, {
        advanceDate: todayIso(),
        amount: 0,
        concept: t("ExpenseUser.note.advanceDefaultConcept"),
      });
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errAdvance"),
      );
    } finally {
      setLedgerBusy(false);
    }
  };

  const updateAdvanceRow = async (advanceId, data) => {
    if (!valid) return;
    try {
      setLedgerBusy(true);
      setError(null);
      await patchExpenseUserAdvance(idNum, advanceId, data);
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errAdvance"),
      );
    } finally {
      setLedgerBusy(false);
    }
  };

  const onDeleteAdvance = async (advanceId) => {
    if (!valid) return;
    try {
      setLedgerBusy(true);
      setError(null);
      await deleteExpenseUserAdvance(idNum, advanceId);
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errAdvanceDelete"),
      );
    } finally {
      setLedgerBusy(false);
    }
  };

  const registerSettlementPayment = async () => {
    if (!valid) return;
    const pending = Number(note?.settlementSummary?.pending || 0);
    if (Math.abs(pending) < 0.01) return;
    try {
      setLedgerBusy(true);
      setError(null);
      await createExpenseUserPayment(idNum, {
        paymentDate: todayIso(),
        amount: Math.abs(pending),
        direction: pending < 0 ? "employee_returns" : "company_pays",
        paymentMethod: "transfer",
      });
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errPayment"),
      );
    } finally {
      setLedgerBusy(false);
    }
  };

  const onDeletePayment = async (paymentId) => {
    if (!valid) return;
    try {
      setLedgerBusy(true);
      setError(null);
      await deleteExpenseUserPayment(idNum, paymentId);
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errPaymentDelete"),
      );
    } finally {
      setLedgerBusy(false);
    }
  };

  const requestRemoveFromNote = (doc) => {
    const docId = doc?.id;
    if (!docId) return;
    const isPosted =
      Boolean(doc?.Contabilizado) ||
      String(doc?.Status || "") === "posted_a3" ||
      String(doc?.Status || "") === "cancelled";
    if (isPosted) {
      setError(t("ExpenseUser.note.errRemovePosted"));
      return;
    }
    setRemoveDialogDoc(doc);
  };

  const confirmRemoveFromNote = async () => {
    const docId = removeDialogDoc?.id;
    if (!docId) return;
    try {
      setRemoveBusy(true);
      setError(null);
      await patchExpenseUserDocument(docId, { expenseUserNoteId: null });
      await load();
      setRemoveDialogDoc(null);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errRemove"),
      );
    } finally {
      setRemoveBusy(false);
    }
  };

  const onDeleteNote = async () => {
    if (!valid) return;
    try {
      setDeleteBusy(true);
      setError(null);
      await deleteExpenseUserNote(idNum);
      navigate("/expenses-user/notes");
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("ExpenseUser.note.errDelete"),
      );
    } finally {
      setDeleteBusy(false);
      setDeleteDialogOpen(false);
    }
  };

  const documents = note?.Documents || [];
  const advances = note?.Advances || [];
  const payments = note?.Payments || [];
  const settlement = note?.settlementSummary || null;
  const hasPostedDocuments = documents.some(
    (d) =>
      Boolean(d?.Contabilizado) ||
      String(d?.Status || "") === "posted_a3" ||
      String(d?.Status || "") === "cancelled",
  );

  const fieldFormSx = {
    minWidth: { xs: "100%", sm: 200 },
    ...pageFieldSx(theme),
  };

  return (
    <Box sx={expenseUserPageShellSx(contentMargin, { maxWidth: 1000 })}>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={() => navigate("/expenses-user/notes")}
        sx={{ mb: 2, alignSelf: "flex-start", borderRadius: 2 }}
      >
        {t("ExpenseUser.backToNotes")}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !note && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={24} />
          {t("ExpenseUser.loading")}
        </Box>
      )}

      {note && (
        <>
          <Box sx={pageHeroShellSx(theme, { sticky: isMobile })}>
            <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <ReceiptLongOutlinedIcon
                  sx={{
                    color: "primary.main",
                    fontSize: { xs: "2rem", sm: "2.25rem" },
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1, display: "block", fontWeight: 700 }}
                  >
                    CRM
                  </Typography>
                  <Typography
                    component="h1"
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.2,
                      fontSize: { xs: "1.2rem", md: "1.5rem" },
                    }}
                  >
                    {note.Title || t("ExpenseUser.noTitle")}{" "}
                    <Typography
                      component="span"
                      color="text.secondary"
                      variant="body1"
                      sx={{ fontWeight: 600 }}
                    >
                      #{note.id}
                    </Typography>
                  </Typography>
                  {note.Description && String(note.Description).trim() ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {note.Description}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.25 }}
                  >
                    {t("ExpenseUser.note.detailHeroSubtitle")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {settlement && (
            <Paper
              variant="outlined"
              sx={{
                ...pageContentCardSx(theme, theme.palette.primary.main),
                p: { xs: 1.25, sm: 2 },
                mb: 2,
                maxWidth: 420,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                {t("ExpenseUser.note.settlementTitle")}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                <Typography>{t("ExpenseUser.note.settleAdvances")}</Typography>
                <Typography fontWeight={600}>
                  {formatEuro(settlement.totalAdvances)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                <Typography>{t("ExpenseUser.note.settleExpenses")}</Typography>
                <Typography fontWeight={600}>
                  {formatEuro(settlement.totalExpenses)}
                </Typography>
              </Box>
              {(Number(settlement.totalCompanyCard) > 0 ||
                Number(settlement.totalCash) > 0) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                  {t("ExpenseUser.note.settleSplit", {
                    cash: formatEuro(settlement.totalCash),
                    card: formatEuro(settlement.totalCompanyCard),
                  })}
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography>
                  {settlement.settlement === "employee_returns"
                    ? t("ExpenseUser.note.balanceReturn")
                    : t("ExpenseUser.note.balancePay")}
                </Typography>
                <Typography fontWeight={700}>
                  {formatEuro(Math.abs(settlement.pending))}
                </Typography>
              </Box>
            </Paper>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Button
              variant="contained"
              onClick={onPdf}
              disabled={pdfBusy || documents.length === 0}
            >
              {pdfBusy
                ? t("ExpenseUser.note.printPdfBusy")
                : t("ExpenseUser.note.printPdf")}
            </Button>
            <Button
              color="error"
              variant="outlined"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={documents.length > 0 || hasPostedDocuments}
            >
              {t("ExpenseUser.note.deleteNote")}
            </Button>
          </Box>

          <Tabs
            value={noteTab}
            onChange={(_e, v) => setNoteTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label={t("ExpenseUser.note.tabExpenses")} />
            <Tab label={t("ExpenseUser.note.tabAdvances")} />
            <Tab label={t("ExpenseUser.note.tabPayments")} />
          </Tabs>

          {noteTab === 0 && (
            <>
            <Paper
              variant="outlined"
              sx={{
                ...pageContentCardSx(theme, theme.palette.primary.main),
                p: { xs: 1.25, sm: 2 },
                mb: 2,
                position: "relative",
              }}
            >
                {uploading && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: { sm: "center" },
                  }}
                >
                  <FormControl size="small" sx={{ ...fieldFormSx, minWidth: 180 }}>
                    <InputLabel id="k-label">
                      {t("ExpenseUser.note.ticketType")}
                    </InputLabel>
                    <Select
                      labelId="k-label"
                      label={t("ExpenseUser.note.ticketType")}
                      value={kind}
                      onChange={(e) => setKind(e.target.value)}
                    >
                      <MenuItem value={KIND_STAFF}>
                        {t("ExpenseUser.note.typeStaff")}
                      </MenuItem>
                      <MenuItem value={KIND_SUPPLIER}>
                        {t("ExpenseUser.note.typeSupplier")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <label
                    htmlFor="expense-user-note-pick-file"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      id="expense-user-note-pick-file"
                      type="file"
                      hidden
                      accept="image/*,application/pdf"
                      onChange={onFile}
                    />
                    <Button variant="contained" component="span">
                      {t("ExpenseUser.note.file")}
                    </Button>
                  </label>
                  {shouldUseCameraCapture() ? (
                    <label
                      htmlFor="expense-user-note-camera"
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        id="expense-user-note-camera"
                        type="file"
                        hidden
                        accept="image/*"
                        capture="environment"
                        onChange={onFile}
                      />
                      <Button variant="outlined" component="span">
                        {t("ExpenseUser.takePhoto")}
                      </Button>
                    </label>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => setWebcamOpen(true)}
                    >
                      {t("ExpenseUser.takePhoto")}
                    </Button>
                  )}
                </Box>
              </Paper>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("ExpenseUser.note.ticketsInNote", { count: documents.length })}
          </Typography>

          {documents.map((row) => (
            <Box
              key={row.id}
              sx={{
                ...(isMobile
                  ? {
                      ...pageMobileCardSx(theme, {
                        accentColor: theme.palette.primary.main,
                      }),
                      pl: 2,
                      mb: 1.25,
                    }
                  : {
                      p: 2,
                      mb: 1,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  flexWrap: "wrap",
                  alignItems: isMobile ? "stretch" : "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Button
                    variant="text"
                    onClick={() => navigate(`/expenses-user/${row.id}`)}
                    sx={{
                      textAlign: "left",
                      p: 0,
                      minWidth: 0,
                      fontSize: isMobile ? "0.9rem" : "1rem",
                      fontWeight: 700,
                      justifyContent: "flex-start",
                    }}
                  >
                    #{row.id} ·{" "}
                    {row.OriginalFileName || t("ExpenseUser.note.noFileName")}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {t("ExpenseUser.note.userLabel")}:{" "}
                    {row.Owner?.username ||
                      row.Owner?.email ||
                      `#${row.UserId}`}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    alignItems: "center",
                  }}
                >
                  <Chip
                    size="small"
                    label={
                      row.Kind === KIND_STAFF
                        ? t("ExpenseUser.kindPersonal")
                        : t("ExpenseUser.kindSupplier")
                    }
                  />
                  <Chip
                    size="small"
                    label={expenseUserStatusLabel(t, row.Status)}
                    {...getExpenseUserStatusChipProps(row.Status, row.Kind)}
                  />
                  <Chip
                    size="small"
                    {...ticketPaymentTypeChipProps(row, t)}
                  />
                  {!isCompanyCardTicket(row) && (
                    <Chip
                      size="small"
                      {...ticketPaymentChipProps(row.PaymentStatus, t)}
                    />
                  )}
                  {isAdminGastos &&
                    !isCompanyCardTicket(row) &&
                    String(row.PaymentStatus || "pending").toLowerCase() !==
                      "paid" && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={payBusyId === row.id}
                        onClick={() => setTicketPayment(row.id, "paid")}
                        fullWidth={isMobile}
                      >
                        {t("ExpenseUser.note.markPaid")}
                      </Button>
                    )}
                  {isAdminGastos &&
                    !isCompanyCardTicket(row) &&
                    String(row.PaymentStatus || "").toLowerCase() ===
                      "paid" && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={payBusyId === row.id}
                        onClick={() => setTicketPayment(row.id, "pending")}
                        fullWidth={isMobile}
                      >
                        {t("ExpenseUser.note.markUnpaid")}
                      </Button>
                    )}
                  <Button
                    size="small"
                    onClick={() => requestRemoveFromNote(row)}
                    disabled={
                      Boolean(row.Contabilizado) ||
                      String(row.Status || "") === "posted_a3" ||
                      String(row.Status || "") === "cancelled"
                    }
                    fullWidth={isMobile}
                  >
                    {t("ExpenseUser.note.removeFromNote")}
                  </Button>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={ticketAmountLine(t, row)}
                  />
                </Box>
              </Box>
            </Box>
          ))}
            </>
          )}

          {noteTab === 1 && (
            <Box>
              <Button
                variant="contained"
                onClick={addAdvanceRow}
                sx={{ mb: 2 }}
                disabled={ledgerBusy}
              >
                {t("ExpenseUser.note.addAdvance")}
              </Button>
              {advances.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("ExpenseUser.note.advancesEmpty")}
                </Typography>
              ) : (
                advances.map((row) => (
                  <Paper
                    key={row.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 1, borderRadius: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <TextField
                        type="date"
                        size="small"
                        defaultValue={String(row.AdvanceDate || "").slice(0, 10)}
                        onBlur={(e) =>
                          updateAdvanceRow(row.id, {
                            advanceDate: e.target.value,
                          })
                        }
                      />
                      <TextField
                        size="small"
                        label={t("ExpenseUser.note.advanceConcept")}
                        defaultValue={row.Concept || ""}
                        onBlur={(e) =>
                          updateAdvanceRow(row.id, { concept: e.target.value })
                        }
                        sx={{ flex: 1, minWidth: 160 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="€"
                        defaultValue={row.Amount}
                        onBlur={(e) =>
                          updateAdvanceRow(row.id, {
                            amount: Number(e.target.value || 0),
                          })
                        }
                        sx={{ width: 120 }}
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                      <IconButton
                        color="error"
                        onClick={() => onDeleteAdvance(row.id)}
                        disabled={ledgerBusy}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          )}

          {noteTab === 2 && (
            <Box>
              {settlement && Math.abs(Number(settlement.pending)) >= 0.01 ? (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("ExpenseUser.note.finalPay")}
                  </Typography>
                  <Typography>
                    {settlement.settlement === "employee_returns"
                      ? t("ExpenseUser.note.userMustReturn")
                      : t("ExpenseUser.note.payToUser")}
                  </Typography>
                  <Typography sx={{ mt: 1 }}>
                    {t("ExpenseUser.note.amountToSettle")}:{" "}
                    <b>{formatEuro(Math.abs(settlement.pending))}</b>
                  </Typography>
                  {isAdminGastos ? (
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      disabled={ledgerBusy}
                      onClick={registerSettlementPayment}
                    >
                      {t("ExpenseUser.note.registerPay")}
                    </Button>
                  ) : (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {t("ExpenseUser.note.onlyAdminPay")}
                    </Alert>
                  )}
                </Paper>
              ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t("ExpenseUser.note.pendingBalanced")}
                </Alert>
              )}
              {payments.map((row) => (
                <Paper
                  key={row.id}
                  variant="outlined"
                  sx={{ p: 2, mb: 1, borderRadius: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body2">
                      {row.PaymentDate} ·{" "}
                      {row.Direction === "employee_returns"
                        ? t("ExpenseUser.note.dirReturn")
                        : t("ExpenseUser.note.dirPay")}{" "}
                      · {formatEuro(row.Amount)}
                    </Typography>
                    {isAdminGastos && (
                      <IconButton
                        color="error"
                        disabled={ledgerBusy}
                        onClick={() => onDeletePayment(row.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </>
      )}

      <Dialog
        open={Boolean(removeDialogDoc)}
        onClose={() => !removeBusy && setRemoveDialogDoc(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>{t("ExpenseUser.note.removeTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("ExpenseUser.note.removeBody", {
              id: removeDialogDoc?.id,
              name:
                removeDialogDoc?.OriginalFileName ||
                t("ExpenseUser.note.noFileName"),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setRemoveDialogDoc(null)}
            disabled={removeBusy}
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 2, borderColor: "divider" }}
          >
            {t("ExpenseUser.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmRemoveFromNote}
            disabled={removeBusy}
            sx={{ borderRadius: 2 }}
          >
            {removeBusy
              ? t("ExpenseUser.note.removing")
              : t("ExpenseUser.note.removeConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteBusy && setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>{t("ExpenseUser.note.deleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("ExpenseUser.note.deleteBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteBusy}
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 2, borderColor: "divider" }}
          >
            {t("ExpenseUser.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={onDeleteNote}
            disabled={deleteBusy || documents.length > 0 || hasPostedDocuments}
            sx={{ borderRadius: 2 }}
          >
            {deleteBusy
              ? t("ExpenseUser.note.deleting")
              : t("ExpenseUser.note.deleteNote")}
          </Button>
        </DialogActions>
      </Dialog>

      <ExpenseUserWebcamDialog
        open={webcamOpen}
        onClose={() => setWebcamOpen(false)}
        onCapture={uploadTicketFile}
        t={t}
      />
    </Box>
  );
};

export default ExpenseUserNoteDetail;
